import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchCategories } from '../../api/categoriesApi'
import styles from './CategoryNav.module.css'

interface CategoryNavProps {
  citySlug: string
  lang: string
  activeCategory: string
  onCategoryChange: (category: string) => void
  date?: string
}

export default function CategoryNav({
  citySlug,
  lang,
  activeCategory,
  onCategoryChange,
  date,
}: CategoryNavProps) {
  const { t } = useTranslation()

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories', citySlug, lang, date],
    queryFn: () => fetchCategories(citySlug, lang, date),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) {
    return (
      <nav className={styles.navWrapper} aria-label="Category navigation">
        <div className={styles.skeleton}>
          {[...Array(5)].map((_, i) => (
            <div key={i} className={styles.skeletonPill} />
          ))}
        </div>
      </nav>
    )
  }

  return (
    <nav className={styles.navWrapper} aria-label="Category navigation">
      <div className={styles.scrollContainer} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeCategory === 'all'}
          className={`${styles.pill} ${activeCategory === 'all' ? styles.active : ''}`}
          onClick={() => onCategoryChange('all')}
        >
          {t('all_categories')}
        </button>

        {(categories ?? []).map(cat => (
          <button
            key={cat.category}
            type="button"
            role="tab"
            aria-selected={activeCategory === cat.category}
            className={`${styles.pill} ${activeCategory === cat.category ? styles.active : ''}`}
            onClick={() => onCategoryChange(cat.category)}
          >
            {cat.category.charAt(0).toUpperCase() + cat.category.slice(1)}{' '}
            <span aria-hidden="true">({cat.count})</span>
          </button>
        ))}
      </div>
    </nav>
  )
}
